import { getMarketplaceDisplayName } from "../../constants/marketplaceEventUrls";
import { PipelineHealthAlertCounts, PipelineHealthRow, PipelineJobType } from "./pipelineHealth.types";

const JOB_TYPE_LABELS: Record<PipelineJobType, string> = {
	collection: "Collection",
	automapper: "Automapper",
	reprocessing: "Reprocessing",
};

export interface StatusStyle {
	background: string;
	border: string;
	color: string;
}

const UNKNOWN_STATUS_STYLE: StatusStyle = { background: "#F0F0F0", border: "#95999A", color: "#5C5F60" };

export const NO_RUNS_STATE = "no_runs";

const RUN_STATE_LABELS: Record<string, string> = {
	completed: "Completed",
	in_progress: "In progress",
	failed: "Failed",
	[NO_RUNS_STATE]: "No runs",
};

const RUN_STATE_STYLES: Record<string, StatusStyle> = {
	completed: { background: "#F0F5EC", border: "#98BA89", color: "#5C7A4E" },
	in_progress: { background: "#EDF3F7", border: "#54778E", color: "#54778E" },
	failed: { background: "#FDECEC", border: "#D60D25", color: "#B00A1E" },
	[NO_RUNS_STATE]: { background: "#F0F0F0", border: "#95999A", color: "#5C5F60" },
};

export const getRunStatusStyle = (status: string): StatusStyle => RUN_STATE_STYLES[status] ?? UNKNOWN_STATUS_STYLE;

export const getRunStatusLabel = (status: string): string => RUN_STATE_LABELS[status] ?? status;

export const getJobTypeLabel = (jobType: PipelineJobType): string => JOB_TYPE_LABELS[jobType] ?? jobType;

export const getMarketplaceLabel = (marketplace: string | null): string =>
	marketplace ? getMarketplaceDisplayName(marketplace) : "All marketplaces";

export const getPipelineHealthRowKey = (row: Pick<PipelineHealthRow, "jobType" | "marketplace">): string =>
	`${row.jobType}::${row.marketplace ?? "ALL"}`;

export const formatDurationMs = (ms: number | null | undefined): string => {
	if (ms == null || Number.isNaN(ms) || ms < 0) return "-";

	const totalMinutes = Math.floor(ms / 60000);
	const days = Math.floor(totalMinutes / 1440);
	const hours = Math.floor((totalMinutes % 1440) / 60);
	const minutes = totalMinutes % 60;

	if (days > 0) return `${days}d ${hours}h`;
	if (hours > 0) return `${hours}h ${minutes}m`;
	if (minutes > 0) return `${minutes}m`;
	return "<1m";
};

export const getRowStateKey = (row: Pick<PipelineHealthRow, "latestRun">): string =>
	row.latestRun ? row.latestRun.status : NO_RUNS_STATE;

export const getRowStateLabel = (row: Pick<PipelineHealthRow, "latestRun">): string => {
	const run = row.latestRun;
	if (!run) return "No runs";
	if (run.status === "in_progress") return run.elapsedMs != null ? `In progress for ${formatDurationMs(run.elapsedMs)}` : "In progress";
	return getRunStatusLabel(run.status);
};

export const getRowStateStyle = (row: Pick<PipelineHealthRow, "latestRun">): StatusStyle => getRunStatusStyle(getRowStateKey(row));

const ROW_SORT_WEIGHT_FAILED = 0;
const ROW_SORT_WEIGHT_NO_RUNS = 1;
const ROW_SORT_WEIGHT_OTHER = 2;

const getRowSortWeight = (row: PipelineHealthRow): number => {
	if (!row.latestRun) return ROW_SORT_WEIGHT_NO_RUNS;
	if (row.latestRun.status === "failed") return ROW_SORT_WEIGHT_FAILED;
	return ROW_SORT_WEIGHT_OTHER;
};

const getRowActivityTimestamp = (row: PipelineHealthRow): number => {
	if (!row.latestRun) return -Infinity;
	const date = new Date(row.latestRun.startedAt);
	return Number.isNaN(date.getTime()) ? -Infinity : date.getTime();
};

export const sortPipelineHealthRows = (rows: Array<PipelineHealthRow>): Array<PipelineHealthRow> =>
	[...rows].sort((a, b) => {
		const weightDiff = getRowSortWeight(a) - getRowSortWeight(b);
		if (weightDiff !== 0) return weightDiff;
		return getRowActivityTimestamp(b) - getRowActivityTimestamp(a);
	});

export const formatAlertBreakdown = (counts: PipelineHealthAlertCounts): string => {
	const parts: Array<string> = [];
	if (counts.failedCount > 0) parts.push(`${counts.failedCount} failed`);
	if (counts.noRunCount > 0) parts.push(`${counts.noRunCount} without runs`);
	return parts.length > 0 ? parts.join(", ") : "No alerts";
};

export const formatDateTime = (iso: string | null | undefined): string => {
	if (!iso) return "Never";

	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return "-";

	return date.toLocaleString("en-US", {
		month: "2-digit",
		day: "2-digit",
		year: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		hour12: true,
	});
};

const pad2 = (value: number): string => value.toString().padStart(2, "0");

export const formatDateForRangeField = (date: Date): string =>
	`${pad2(date.getMonth() + 1)}/${pad2(date.getDate())}/${date.getFullYear()}`;

export const getTodayRangeValue = (): string => {
	const today = formatDateForRangeField(new Date());
	return `${today} - ${today}`;
};

export const parseDateRangeValue = (value: string | undefined | null): { start: Date; end: Date } | null => {
	if (!value) return null;

	const [startPart, endPart] = value.split(" - ");
	if (!startPart || !endPart) return null;

	const start = new Date(startPart);
	const end = new Date(endPart);
	if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;

	return { start, end };
};

export const toIsoRangeBounds = (range: { start: Date; end: Date }): { from: string; to: string } => {
	const from = new Date(range.start);
	from.setHours(0, 0, 0, 0);
	const to = new Date(range.end);
	to.setHours(23, 59, 59, 999);
	return { from: from.toISOString(), to: to.toISOString() };
};
